import { useState } from 'react'

import { Box, Button, Tag } from '@factorialco/deprecated-design-system'
import {
  ApprovalGroupEmployeesDataTableQuery,
  ProjectionKind,
  useApprovalGroupEmployeesDataTable,
} from 'generated/resources/datatable.graphql'
import { ApprovalsGroupFragment } from 'generated/resources/graphql'

import DataTable, { useDataTable } from 'modules/core/components/DataTable'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { InlineEmployee } from 'modules/employees/components/InlineEmployee'

import AddEmployeesModal from './AddEmployeesModal'
import EmployeeAssignModal from './_EmployeeAssignModal'

interface Props {
  approvalGroup: ApprovalsGroupFragment
  contractVersions: ContractVersions
}

// This should be a fragment but the datatable schema doesn't export them.
type Membership = NonNullable<
  ApprovalGroupEmployeesDataTableQuery['approvals']
>['approvalGroupMembershipsConnection']['edges'][number]['node']

const ApprovalGroupEmployeesTable = ({ approvalGroup, contractVersions }: Props) => {
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()

  const source = useApprovalGroupEmployeesDataTable(
    [
      {
        identifier: 'employee_fullName',
        title: i18n.t('it_management.software_setups.tabs.employees.table.name'),
        kind: ProjectionKind.Custom,
        render: (membership) => {
          const employee = membership.access?.employee
          if (!employee) {
            return null
          }
          return <InlineEmployee employee={employee} variant="medium" />
        },
        template: 'employee',
      },
      {
        identifier: 'employee_job',
        title: i18n.t('employee_form.job_title'),
        kind: ProjectionKind.Custom,
        render: (membership) => {
          const jobTitle = membership.access?.employee?.referenceContract?.jobTitle
          if (!jobTitle) {
            return null
          }
          return <Tag inline>{jobTitle}</Tag>
        },
        template: 'job title',
      },
    ],
    {
      variables: {
        approvalGroupId: approvalGroup.id,
        search: debouncedSearch,
      },
    }
  )

  const BulkActions = () => {
    const { selectedRows } = useDataTable<ApprovalGroupEmployeesDataTableQuery, Membership>()

    const selectedIds = useUnsafeMemo(() => {
      return [...selectedRows].map(Number)
    }, [selectedRows])

    const [showAssignModal, setShowAssignModal] = useState(false)
    const [showAddModal, setShowAddModal] = useState(false)

    return (
      <>
        <Button type="secondary" onClick={() => setShowAddModal(true)}>
          {i18n.t('settings.approvals.approval_group.employees.add_employees.tooltip')}
        </Button>
        {selectedIds.length > 0 && (
          <Button type="secondary" onClick={() => setShowAssignModal(true)} iconName="pencil">
            {i18n.t('settings.approvals.approval_group.employees.assign_employees.tooltip')}
          </Button>
        )}
        {showAssignModal && (
          <EmployeeAssignModal
            selectedMembershipIds={selectedIds}
            onClose={() => setShowAssignModal(false)}
            currentApprovalGroup={approvalGroup}
          />
        )}
        {showAddModal && (
          <AddEmployeesModal
            onClose={() => setShowAddModal(false)}
            approvalGroupId={approvalGroup.id}
            contractVersions={contractVersions}
          />
        )}
      </>
    )
  }

  return (
    <DataTable.Provider source={source} sort={{ path: 'id', direction: 'desc' }}>
      <Box paddingY="s8" gap="s16">
        <DataTable.HeadBar
          disableExport
          disableEditColumns
          actions={<BulkActions />}
          search={{
            placeholder: i18n.t('common.search'),
            value: search,
            onChange: onSearch,
          }}
        />
      </Box>
      <DataTable.Table selectable={() => true} />
    </DataTable.Provider>
  )
}

export default ApprovalGroupEmployeesTable
